import React, { useMemo, useState } from "react";
import { Search, BookOpen, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  getReferenceCategories,
  REFERENCE_LIBRARY_VERSION,
  REFERENCE_LAST_UPDATED_ISO,
} from "@/lib/reference";

/**
 * LibraryPicker — reusable reference-data picker modal (R1a).
 * Built on the same shadcn Dialog primitive as UpgradePromptModal.
 *
 * Props:
 *   open          — boolean
 *   onClose       — () => void
 *   libraryKinds  — array of allowed kinds, e.g. ["drill_pipe"]; the picker
 *                   restricts category options to those kinds.
 *   onPick        — (item, libraryKind) => void; parent decides field mapping.
 *   anchorLabel   — short string shown in the header explaining what field
 *                   the picker is anchored to, e.g. "Pipe OD".
 */
export default function LibraryPicker({
  open,
  onClose,
  libraryKinds = [],
  onPick,
  anchorLabel = "",
}) {
  const [search, setSearch] = useState("");

  const allowedCategories = useMemo(() => {
    const all = getReferenceCategories();
    if (!libraryKinds || libraryKinds.length === 0) return all;
    return all.filter((cat) => cat.libraryKinds.some((k) => libraryKinds.includes(k)));
  }, [libraryKinds]);

  // Pick first allowed category by default; if none, fall back gracefully.
  const [activeCategoryId, setActiveCategoryId] = useState(allowedCategories[0]?.id || "");
  const activeCategory = allowedCategories.find((c) => c.id === activeCategoryId) || allowedCategories[0];

  const visibleItems = useMemo(() => {
    if (!activeCategory) return [];
    const q = search.trim().toLowerCase();
    if (!q) return activeCategory.items;
    return activeCategory.items.filter((it) => {
      const hay = [it.label, it.id, it.source, it.notes || ""].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [activeCategory, search]);

  const handlePick = (item) => {
    if (onPick) onPick(item, activeCategory?.id || libraryKinds[0]);
    if (onClose) onClose();
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v && onClose) onClose(); }}>
      <DialogContent
        data-testid="library-picker-modal"
        className="bg-[var(--rs-panel,#101418)] border border-[var(--rs-border,#1f2630)] rounded-lg p-0 overflow-hidden w-[92vw] max-w-[600px] max-h-[88vh] flex flex-col"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[var(--rs-border,#1f2630)]">
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5 rounded-md bg-amber-500/15 text-amber-400 p-2">
              <BookOpen className="w-5 h-5" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle
                data-testid="library-picker-title"
                className="text-base font-semibold leading-snug text-[var(--rs-text,#e8edf2)]"
              >
                Reference Library {anchorLabel ? `— pick a ${anchorLabel}` : ""}
              </DialogTitle>
              <DialogDescription className="text-xs mt-1 leading-relaxed text-[var(--rs-text-dim,#9aa6b2)]">
                Support data only. Verify the selected value against rig-specific tally books and manufacturer specifications before use.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {allowedCategories.length > 1 && (
          <div
            data-testid="library-picker-tabs"
            className="px-5 pt-3 flex flex-wrap gap-1.5 border-b border-[var(--rs-border,#1f2630)] pb-3"
          >
            {allowedCategories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                data-testid={`library-picker-tab-${cat.id}`}
                onClick={() => setActiveCategoryId(cat.id)}
                className={
                  "h-7 px-3 rounded-full text-[11px] uppercase tracking-[0.10em] font-semibold border " +
                  (cat.id === activeCategoryId
                    ? "border-[var(--rs-amber,#f5b400)] text-[var(--rs-amber,#f5b400)] bg-amber-500/10"
                    : "border-[var(--rs-border,#1f2630)] text-[var(--rs-text-dim,#9aa6b2)] hover:text-[var(--rs-text,#e8edf2)]")
                }
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        <div className="px-5 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--rs-text-dim,#9aa6b2)]" aria-hidden="true" />
            <input
              data-testid="library-picker-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by label, id, or notes…"
              className="w-full h-9 pl-8 pr-3 text-sm rounded-md bg-[var(--rs-panel-2,#161b21)] border border-[var(--rs-border,#1f2630)] text-[var(--rs-text,#e8edf2)] placeholder:text-[var(--rs-text-dim,#9aa6b2)] focus:outline-none focus:border-[var(--rs-amber,#f5b400)]"
            />
          </div>
        </div>

        <div
          data-testid="library-picker-list"
          className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 pt-1"
        >
          {visibleItems.length === 0 && (
            <div
              data-testid="library-picker-empty"
              className="text-xs text-[var(--rs-text-dim,#9aa6b2)] py-6 text-center"
            >
              No reference entries available for this category yet. Future phase will add user-entered and rig-custom items.
            </div>
          )}
          <ul className="flex flex-col gap-2">
            {visibleItems.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  data-testid={`library-picker-item-${it.id}`}
                  onClick={() => handlePick(it)}
                  className="w-full text-left rounded-md border border-[var(--rs-border,#1f2630)] bg-[var(--rs-panel-2,#161b21)] hover:bg-[var(--rs-panel-3,#1a2028)] px-3 py-2.5 transition-colors"
                >
                  <div className="text-sm font-semibold text-[var(--rs-text,#e8edf2)]">{it.label}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[var(--rs-text-dim,#9aa6b2)]">
                    {it.id} · source: {it.source} · {it.version} · updated {it.lastUpdatedISO}
                  </div>
                  {it.notes && (
                    <div className="mt-1 text-xs text-[var(--rs-text-dim,#9aa6b2)] leading-relaxed">{it.notes}</div>
                  )}
                  {/* Surface key geometry inline for at-a-glance check */}
                  {it.kind === "drill_pipe" && (
                    <div className="mt-1 text-[11px] text-[var(--rs-text,#e8edf2)]">
                      OD {it.od_in} in · ID {it.id_in} in · {it.weight_ppf} ppf
                    </div>
                  )}
                  {it.kind === "hole_size" && (
                    <div className="mt-1 text-[11px] text-[var(--rs-text,#e8edf2)]">
                      Diameter {it.diameter_in} in
                    </div>
                  )}
                  {it.kind === "pump_output" && (
                    <div className="mt-1 text-[11px] text-[var(--rs-text,#e8edf2)]">
                      Liner {it.liner_diameter_in} in · stroke {it.stroke_length_in} in · η {it.volumetric_efficiency}
                      {Number.isFinite(it.output_bbl_per_stk) && (
                        <> · derived ≈ {it.output_bbl_per_stk.toFixed(5)} bbl/stk</>
                      )}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="px-5 pb-4 pt-2 border-t border-[var(--rs-border,#1f2630)] flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--rs-text-dim,#9aa6b2)]">
            Library {REFERENCE_LIBRARY_VERSION} · Updated {REFERENCE_LAST_UPDATED_ISO}
          </div>
          <button
            type="button"
            data-testid="library-picker-close"
            onClick={() => onClose && onClose()}
            className="h-8 px-3 rounded-md text-xs border border-[var(--rs-border,#1f2630)] text-[var(--rs-text-dim,#9aa6b2)] hover:text-[var(--rs-text,#e8edf2)] hover:bg-[var(--rs-panel-2,#161b21)] inline-flex items-center gap-1.5"
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" /> Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
